'use client';

import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { createClient, isAuthInProgress, setAuthInProgress, clearAllAuthStorage } from '@/lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  // Global auth loading state — true while any auth operation is running
  const [authLoading, setAuthLoading] = useState(false);
  const [userRole, setUserRole] = useState<string>('admin');
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});

  // Memoize supabase client so it's not recreated on every render
  const supabase = useMemo(() => createClient(), []);

  // Track last fetched userId to avoid duplicate role/permission fetches
  const lastFetchedUserId = useRef<string | null>(null);
  // Client-side rate limit: track last signIn attempt timestamp
  const lastSignInAttempt = useRef<number>(0);
  // Prevent multiple session checks on mount — component-instance guard only
  const sessionChecked = useRef(false);

  const SIGN_IN_COOLDOWN_MS = 3000; // 3 seconds between attempts

  // Use refs for callbacks used inside the one-time useEffect
  const supabaseRef = useRef(supabase);
  const lastFetchedUserIdRef = useRef(lastFetchedUserId);

  useEffect(() => { supabaseRef.current = supabase; }, [supabase]);

  // ─── Error Classification ──────────────────────────────────────────────────
  const isBrokenSessionError = (error: any): boolean => {
    if (!error) return false;
    const msg = (error.message || '').toLowerCase();
    const code = (error.code || '').toLowerCase();
    return (
      code === 'refresh_token_not_found' || msg.includes('refresh token not found') ||
      msg.includes('invalid refresh token') ||
      msg.includes('token has expired') ||
      msg.includes('lock_broken') ||
      msg.includes('lock broken') ||
      msg.includes('steal') ||
      error?.status === 400
    );
  };

  const isRateLimitError = (error: any): boolean => {
    if (!error) return false;
    const msg = (error.message || '').toLowerCase();
    const code = (error.code || '').toLowerCase();
    return (
      code === 'request_rate_limit' || msg.includes('rate limit') ||
      msg.includes('too many requests') ||
      msg.includes('over_email_send_rate_limit') ||
      error?.status === 429
    );
  };

  const fetchUserRoleAndPermissions = useCallback(async (userId: string) => {
    if (lastFetchedUserId.current === userId) return;
    lastFetchedUserId.current = userId;

    try {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', userId)
        .maybeSingle();

      const role = profile?.role || 'admin';
      setUserRole(role);

      if (role === 'admin') {
        setPermissions({
          dispatch: true, loads: true, leads: true,
          finance: true, integrations: true, settings: true,
        });
        return;
      }

      const { data: perms } = await supabase
        .from('page_permissions')
        .select('page, can_access')
        .eq('role', role);

      const permMap: Record<string, boolean> = {};
      (perms || []).forEach((p: { page: string; can_access: boolean }) => {
        permMap[p.page] = p.can_access;
      });
      setPermissions(permMap);
    } catch {
      setPermissions({
        dispatch: true, loads: true, leads: true,
        finance: true, integrations: true, settings: true,
      });
    }
  }, [supabase]);

  const fetchRoleRef = useRef(fetchUserRoleAndPermissions);
  useEffect(() => { fetchRoleRef.current = fetchUserRoleAndPermissions; }, [fetchUserRoleAndPermissions]);

  // ─── Force Logout — clears ALL auth state and storage ─────────────────────
  const doForceLogout = useCallback(async (sb: any) => {
    clearAllAuthStorage();
    setSession(null);
    setUser(null);
    lastFetchedUserId.current = null;
    setUserRole('admin');
    setPermissions({});
    setLoading(false);
    setAuthLoading(false);
    setAuthInProgress(false);
    try {
      await sb.auth.signOut();
    } catch {
      // Ignore signOut errors — state is already cleared
    }
    if (typeof window !== 'undefined') {
      window.location.href = '/sign-up-login-screen';
    }
  }, []);

  const doForceLogoutRef = useRef(doForceLogout);
  useEffect(() => { doForceLogoutRef.current = doForceLogout; }, [doForceLogout]);

  // ─── ONE-TIME Mount Effect ─────────────────────────────────────────────────
  // Uses a ref guard so React Strict Mode double-invocation cannot trigger
  // a second getSession() call within the same component instance.
  // Does NOT call signIn — only reads the existing session.
  useEffect(() => {
    if (sessionChecked.current) return;
    sessionChecked.current = true;

    let mounted = true;
    const sb = supabaseRef.current;

    // Load session ONCE on mount — do NOT call signIn here
    sb.auth.getSession().then(({ data: { session: initialSession }, error }: { data: { session: any }, error: any }) => {
      if (!mounted) return;

      if (error) {
        if (isBrokenSessionError(error)) {
          doForceLogoutRef.current(sb);
        } else {
          setSession(null);
          setUser(null);
          setLoading(false);
        }
        return;
      }

      // No session — clear any stale storage that may be lingering
      if (!initialSession) {
        clearAllAuthStorage();
        setSession(null);
        setUser(null);
        setLoading(false);
        return;
      }

      setSession(initialSession);
      setUser(initialSession.user ?? null);
      if (initialSession.user) {
        fetchRoleRef.current(initialSession.user.id);
      }
      setLoading(false);
    });

    // Listen for auth state changes
    const {
      data: { subscription },
    } = sb.auth.onAuthStateChange((event: string, newSession: any) => {
      if (!mounted) return;

      // ── Broken session events → force logout ──────────────────────────────
      if (
        (event as string) === 'TOKEN_REFRESH_FAILED' ||
        (event === 'TOKEN_REFRESHED' && !newSession)
      ) {
        doForceLogoutRef.current(sb);
        return;
      }

      // ── Sign out ──────────────────────────────────────────────────────────
      if (event === 'SIGNED_OUT') {
        clearAllAuthStorage();
        setSession(null);
        setUser(null);
        lastFetchedUserId.current = null;
        setUserRole('admin');
        setPermissions({});
        setLoading(false);
        setAuthLoading(false);
        setAuthInProgress(false);
        return;
      }

      setSession(newSession);
      setUser(newSession?.user ?? null);

      if (event === 'SIGNED_IN' && newSession?.user) {
        if (lastFetchedUserId.current !== newSession.user.id) {
          lastFetchedUserId.current = null;
          fetchRoleRef.current(newSession.user.id);
        }
        // Clear the auth-in-progress flag once sign-in completes
        setAuthInProgress(false);
        setAuthLoading(false);
      }

      setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps — intentional. All callbacks accessed via refs.

  // ─── forceLogout (public API) ──────────────────────────────────────────────
  const forceLogout = useCallback(async () => {
    clearAllAuthStorage();
    setSession(null);
    setUser(null);
    lastFetchedUserId.current = null;
    setUserRole('admin');
    setPermissions({});
    setLoading(false);
    setAuthLoading(false);
    setAuthInProgress(false);
    try {
      await supabase.auth.signOut();
    } catch {
      // Ignore signOut errors
    }
    if (typeof window !== 'undefined') {
      window.location.href = '/sign-up-login-screen';
    }
  }, [supabase]);

  const hasPermission = useCallback((page: string): boolean => {
    if (userRole === 'admin') return true;
    return permissions[page] ?? false;
  }, [userRole, permissions]);

  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          company_name: metadata?.companyName || '',
          avatar_url: metadata?.avatarUrl || '',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    // Prevent concurrent auth requests — the primary cause of lock contention
    if (isAuthInProgress()) {
      throw new Error('An authentication request is already in progress. Please wait.');
    }

    const now = Date.now();
    if (now - lastSignInAttempt.current < SIGN_IN_COOLDOWN_MS) {
      throw new Error('Please wait a moment before trying again.');
    }
    lastSignInAttempt.current = now;

    setAuthInProgress(true);
    setAuthLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        setAuthInProgress(false);
        setAuthLoading(false);
        // Handle broken session errors during sign-in
        if (isBrokenSessionError(error)) {
          clearAllAuthStorage();
        }
        // Handle rate limit errors during sign-in
        if (isRateLimitError(error)) {
          throw Object.assign(new Error('Too many login attempts. Please wait a moment and try again.'), { status: 429 });
        }
        throw error;
      }
      // authLoading cleared in onAuthStateChange SIGNED_IN handler
      return data;
    } catch (err) {
      setAuthInProgress(false);
      setAuthLoading(false);
      throw err;
    }
  };

  const signOut = async () => {
    lastFetchedUserId.current = null;
    clearAllAuthStorage();
    setAuthInProgress(false);
    setAuthLoading(false);
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const getCurrentUser = async () => {
    const { data: { user: currentUser }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return currentUser;
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      authLoading,
      userRole,
      permissions,
      signIn,
      signUp,
      signOut,
      forceLogout,
      hasPermission,
      getCurrentUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
